import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll restoration for React Router v6 SPAs.
 *
 * Strategy: save scroll position on mousedown/touchstart (before click/scroll),
 * restore on back navigation.
 *
 * - On mousedown/touchstart over internal link: save current scroll position.
 * - On POP (back/forward): restore saved position.
 * - On PUSH/REPLACE: scroll to top.
 */

function getSavedPosition(key: string): { x: number; y: number } | null {
  try {
    const raw = sessionStorage.getItem(`scroll:${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePosition(key: string) {
  try {
    sessionStorage.setItem(
      `scroll:${key}`,
      JSON.stringify({ x: window.scrollX, y: window.scrollY }),
    );
  } catch {
    // Ignore
  }
}

function getInternalLink(event: MouseEvent | TouchEvent): HTMLAnchorElement | null {
  let target = event.composedPath
    ? (event.composedPath()[0] as HTMLElement)
    : (event.target as HTMLElement);

  while (target && target.tagName !== "A" && target !== document.body) {
    target = target.parentElement as HTMLElement;
  }
  if (!target || target.tagName !== "A") return null;

  const anchor = target as HTMLAnchorElement;
  const href = anchor.getAttribute("href");
  if (!href) return null;

  // Only internal SPA links
  if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return null;
  if (href.startsWith("#")) return null;
  if (anchor.target === "_blank") return null;
  if (anchor.hasAttribute("download")) return null;

  return anchor;
}

export function ScrollRestoration() {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const key = pathname + search;

  // Save scroll position on pointer down over internal links
  useEffect(() => {
    function handlePointerDown(event: MouseEvent | TouchEvent) {
      const link = getInternalLink(event);
      if (link) {
        const currentKey = location.pathname + location.search;
        savePosition(currentKey);
      }
    }

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
    };
  }, []);

  // Handle scroll position on route change
  useEffect(() => {
    if (navigationType === "POP") {
      const saved = getSavedPosition(key);
      if (saved && (saved.x > 0 || saved.y > 0)) {
        // Double rAF to wait for DOM paint before scrolling
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(saved.x, saved.y);
          });
        });
      } else {
        window.scrollTo(0, 0);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [key, navigationType]);

  return null;
}